import { Controller } from "@hotwired/stimulus"

// Application-wide controller to replace marmoset_music.js initialization
// Responsibilities:
// - Global app initialization
// - Turbo event handling
// - Navigation state management  
// - Page metadata updates
// - Global event binding
export default class extends Controller {
  static targets = ["container", "content"]
  
  connect() {
    this.initializeApp()
    this.bindTurboEvents()
  }

  disconnect() {
    this.unbindTurboEvents()
    this.unbindGlobalEvents()
  }

  initializeApp() {
    // Set CSRF token for Rails requests
    this.setCSRF()
    
    // Initialize page state
    this.updateCurrentNav()
    this.handlePortalLink()
    this.adjustForAnnouncement()
    
    // Bind global events
    this.bindGlobalEvents()
  }

  bindTurboEvents() {
    // Use global counter to prevent multiple instances from binding the same events
    if (!window.marmosetTurboEventCount) {
      window.marmosetTurboEventCount = 0
    }
    if (window.marmosetTurboEventCount === 0) {
      // Bind methods to preserve context for proper cleanup
      this.boundHandleTurboLoad = this.handleTurboLoad.bind(this)
      this.boundHandleTurboFrameLoad = this.handleTurboFrameLoad.bind(this)
      this.boundHandleBeforeVisit = this.handleBeforeVisit.bind(this)

      // Listen for Turbo navigation events
      document.addEventListener("turbo:load", this.boundHandleTurboLoad)
      document.addEventListener("turbo:frame-load", this.boundHandleTurboFrameLoad)
      document.addEventListener("turbo:before-visit", this.boundHandleBeforeVisit)
   }
   window.marmosetTurboEventCount++
  }

  unbindTurboEvents() {
    if (!window.marmosetTurboEventCount || window.marmosetTurboEventCount === 0) return
    
    window.marmosetTurboEventCount--

    // Remove event listeners using the bound methods
    document.removeEventListener("turbo:load", this.boundHandleTurboLoad)
    document.removeEventListener("turbo:frame-load", this.boundHandleTurboFrameLoad)
    document.removeEventListener("turbo:before-visit", this.boundHandleBeforeVisit)
  }

  handleTurboLoad(event) {
    // Update navigation state
    this.updateCurrentNav()
    
    // Scroll to top
    this.scrollToTop()
    
    // Update page metadata
    this.updatePageMetadata()
  }

  handleTurboFrameLoad(event) {
    console.log("Turbo frame load:", event.target?.id || "unknown frame")
    // Handle any frame-specific logic
  }

  handleBeforeVisit(event) {
    console.log("Before visit:", event.detail.url)
    // Show loading state if needed
  }

  setCSRF() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    if (csrfToken) {
      // Use global flag to prevent multiple CSRF event bindings
      if (!window.marmosetCSRFEventsBound) {
        window.marmosetCSRFEventsBound = true
        // Set CSRF token for fetch requests
        document.addEventListener('turbo:before-fetch-request', (event) => {
          event.detail.fetchOptions.headers['X-CSRF-Token'] = csrfToken
        })
      }
    }
  }

  updateCurrentNav() {
    const nav = document.querySelector('nav[role="main"]')
    if (!nav) return

    const links = nav.querySelectorAll('a')
    const currentPath = window.location.pathname

    links.forEach(link => link.classList.remove('current'))
    
    const currentLink = nav.querySelector(`a[href="${currentPath}"]`)
    if (currentLink) {
      currentLink.classList.add('current')
    }
  }

  handlePortalLink() {
    const link = document.querySelector('.dashboard-link')
    if (link?.classList.contains('available')) {
      const userRole = document.body.dataset.userRole
      if (userRole === 'vendor') {
        link.textContent = 'Artist Portal'
      } else {
        link.textContent = 'Portal'
      }
    }
  }

  adjustForAnnouncement() {
    const homeNew = document.getElementById('mm-home-new')
    const header = document.querySelector('header.new-header')
    //const drawerNav = document.getElementById('mm-drawer-nav')
    
    if (homeNew) {
      const hasAnnouncement = homeNew.children[0]?.classList.contains('announcementBanner')
      
      if (hasAnnouncement) {
        header?.classList.add('announcement')
        //drawerNav?.classList.add('announcement')
      } else {
        header?.classList.remove('announcement')
        //drawerNav?.classList.remove('announcement')
      }
    }
  }

  bindGlobalEvents() {
    // Use global counter to prevent multiple instances from binding the same events
    if (!window.marmosetGlobalEventCount) {
      window.marmosetGlobalEventCount = 0
    }

    if (window.marmosetGlobalEventCount === 0) {
      // Bind methods to preserve context for proper cleanup
      this.boundHandleGlobalClick = this.handleGlobalClick.bind(this)

    // Bind global click handlers
    document.addEventListener('click', this.boundHandleGlobalClick)
    }
  }

  unbindGlobalEvents() {
    if (!window.marmosetGlobalEventCount || window.marmosetGlobalEventCount === 0) return

    window.marmosetGlobalEventCount--

    if (window.marmosetGlobalEventCount === 0) {
      document.removeEventListener('click', this.boundHandleGlobalClick)
    }
  }

  handleGlobalClick(event) {
    const target = event.target.closest('[data-prevent-default]')
    if (target) {
      event.preventDefault()
    }

    // Handle newsletter links
    const newsletterLink = event.target.closest('[data-marmoset-newsletter]')
    if (newsletterLink) {
      event.preventDefault()
      // TODO: implement newsletter modal
      return false
    }
  }

  scrollToTop() {
    // Check if scrolling should be suppressed
    if (this.shouldSuppressScroll()) {
      return
    }

    const container = document.getElementById('container')
    if (container) {
      container.scrollTop = 0
    } else {
     window.scrollTo(0, 0)
    }
  }

  shouldSuppressScroll() {
    // Checks for elements with data-no-scroll attribute to suppress scrolling
    return document.querySelector(
      'form[data-submitting="true"], form[data-no-scroll], body[data-no-scroll], #container[data-no-scroll], [data-no-scroll="true"], [data-processing="true"]'
    ) !== null
  }

  updatePageMetadata() {
    // Update page title from data attribute or meta tag
    const titleElement = document.querySelector('[data-title]')
    const title = titleElement?.dataset.title || 
                  document.querySelector('meta[name="title"]')?.getAttribute('content') || ''

    if (title) {
      document.title = title ? `${title} | Marmoset` : 'Marmoset'
    }

    // Update section data attribute
    const container = document.getElementById('container')
    const section = document.body.dataset.section
    if (container && section) {
      container.setAttribute('data-section', section)
    }
  }
}